import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FileText } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Contact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName: string | null;
  parentSlug: string | null;
}

interface SubCategory extends Category {
  articleCount?: number;
}

interface Article {
  id: string;
  slug: string;
  title: string;
  categoryName: string | null;
  updatedAt: string;
}

export function PortalKbCategoryPage(): React.ReactElement {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<SubCategory[] | null>(null);
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(category?.name ?? 'Knowledge base');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setCategory(null);
    setSubcategories(null);
    setArticles(null);

    apiFetch<{ contact: Contact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (cancelled) return;
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: `/portal/kb/c/${slug}` } });
          return;
        }
        setContact(r.contact);
        return apiFetch<{ category: Category; subcategories: SubCategory[] }>(
          `/api/portal/kb/categories/by-slug/${encodeURIComponent(slug)}`,
        );
      })
      .then(async (r) => {
        if (cancelled || !r) return;
        setCategory(r.category);
        setSubcategories(r.subcategories);
        const arts = await apiFetch<{ articles: Article[] }>(
          `/api/portal/kb/articles?categoryId=${r.category.id}`,
        );
        if (!cancelled) setArticles(arts.articles);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          navigate('/login', { replace: true, state: { from: `/portal/kb/c/${slug}` } });
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load category.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  if (error) {
    return (
      <div className="bg-white">
        <PortalNavBar contact={contact} active="kb" />
        <div className="mx-auto max-w-3xl px-6 py-20 sm:px-8">
          <Link to="/portal" className="text-sm text-[var(--color-muted)] hover:underline">
            ← Help center
          </Link>
          <div className="mt-4 rounded-2xl border border-[var(--color-danger)]/30 bg-red-50/40 p-6">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  if (!category || !articles || !subcategories) {
    return (
      <div className="bg-white">
        <PortalNavBar contact={contact} active="kb" />
        <p className="p-10 text-[var(--color-muted)]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <PortalNavBar contact={contact} active="kb" />

      {/* HEADER */}
      <section className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8">
          <nav className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
            <Link to="/portal" className="hover:text-[var(--color-header)]">Help center</Link>
            <span>/</span>
            <Link to="/portal/kb" className="hover:text-[var(--color-header)]">Knowledge base</Link>
            {category.parentSlug && (
              <>
                <span>/</span>
                <Link to={`/portal/kb/c/${category.parentSlug}`} className="hover:text-[var(--color-header)]">
                  {category.parentName}
                </Link>
              </>
            )}
            <span>/</span>
            <span className="text-[var(--color-header)]">{category.name}</span>
          </nav>
          <h1 className="font-display mt-4 text-4xl font-extrabold text-[var(--color-header)] sm:text-5xl">
            {category.name}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            {articles.length} {articles.length === 1 ? 'article' : 'articles'}
            {subcategories.length > 0 && ` · ${subcategories.length} ${subcategories.length === 1 ? 'topic' : 'topics'}`}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl space-y-12 px-6 py-12 sm:px-8">
        {subcategories.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-[var(--color-header)]">Topics</h2>
            <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {subcategories.map((sub) => (
                <Link
                  key={sub.id}
                  to={`/portal/kb/c/${sub.slug}`}
                  className="group rounded-2xl border border-[var(--color-border)] bg-white p-6 transition hover:-translate-y-0.5 hover:border-[var(--color-t3-blue)] hover:shadow-lg"
                >
                  <span
                    className="grid h-12 w-12 place-items-center rounded-xl text-white"
                    style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
                  >
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h20M5 21V8M19 21V8M3 8l9 13 9-13"/></svg>
                  </span>
                  <h3 className="font-display mt-5 text-xl font-bold text-[var(--color-header)]">{sub.name}</h3>
                  <div className="mt-4 text-xs font-semibold text-[var(--color-t3-blue)] transition group-hover:translate-x-1">
                    Open →
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-display text-2xl font-bold text-[var(--color-header)]">Articles</h2>
          {articles.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-white p-10 text-center text-sm text-[var(--color-muted)]">
              No articles in this category yet.
            </div>
          ) : (
            <ul className="mt-5 divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
              {articles.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/portal/kb/${a.slug}`}
                    className="flex items-center gap-4 py-5 transition hover:translate-x-1"
                  >
                    <FileText className="h-5 w-5 shrink-0 text-[var(--color-t3-purple)]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[var(--color-header)]">{a.title}</div>
                      {a.categoryName && a.categoryName !== category.name && (
                        <div className="mt-0.5 text-xs text-[var(--color-muted)]">{a.categoryName}</div>
                      )}
                    </div>
                    <div className="text-xs text-[var(--color-muted)]">
                      {new Date(a.updatedAt).toLocaleDateString()}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <PortalFooter />
    </div>
  );
}
